/**
 * Metriekencatalogus-fundering (M11 / US04K).
 *
 * De catalogus is de bron van waarheid voor metriekcodes, waardetypen, eenheden,
 * standaardclassificatie en bronvoorkeur. Connectors mogen alleen observaties
 * aanleveren voor metrieken die hier gedefinieerd zijn; onbekende codes worden
 * bij normalisatie geweigerd (nooit stilzwijgend opgeslagen).
 */
export const OBSERVATION_VALUE_TYPES = ['NUMBER', 'TEXT', 'BOOLEAN', 'RANGE', 'LIST'];
export const OBSERVATION_CLASSIFICATIONS = ['FACT', 'ESTIMATE', 'INFERENCE'];
export const OBSERVATION_AVAILABILITIES = [
    'AVAILABLE',
    'NOT_AVAILABLE',
    'NOT_APPLICABLE',
    /**
     * Nooit opgevraagd — de beschikbaarheidsprobe (P6) of het fetchplan (P7) heeft
     * besloten deze bron/dit veld niet aan te roepen. Bewust onderscheiden van
     * `NOT_AVAILABLE` ("gevraagd, niets gevonden"): zonder dat onderscheid kan een
     * lezer niet zien of een dimensie is geprobeerd of stil is overgeslagen.
     */
    'NOT_QUERIED',
    'ERROR',
];
/**
 * Herkomstniveau van een waarde (brief fase 2). Staat los van `classification`:
 * een KVK-feit (SOURCED) en een in code berekende ratio (DERIVED) kunnen beide
 * `FACT` zijn. Tier bepaalt *wie* de waarde produceerde, classificatie *hoe
 * zeker* de claim is.
 *
 * `AI_GENERATED` moet in de tekst van de cel zelf herkenbaar zijn, niet alleen
 * in de opmaak: rapporten worden naar decks en e-mails gekopieerd en dan
 * overleeft woordkeus wél en styling niet.
 */
export const PROVENANCE_TIERS = ['SOURCED', 'DERIVED', 'AI_GENERATED'];
/** Letteraanduiding uit de brief (A/B/C) voor weergave in rapport en export. */
export const PROVENANCE_TIER_LETTER = {
    SOURCED: 'A',
    DERIVED: 'B',
    AI_GENERATED: 'C',
};
// ─────────────────────────────────────────────────────────────────────────
// Bronfamilies (docs/RELIABILITY_MODEL.md)
// ─────────────────────────────────────────────────────────────────────────
/**
 * Twee routes naar dezelfde onderliggende bron zijn één bronfamilie en tellen
 * dus nooit als onafhankelijke bevestiging (bijv. Google Ads direct en via
 * Windsor.ai).
 */
export const SOURCE_FAMILIES = [
    'WEBSITE',
    'GOOGLE_SEARCH',
    'SE_RANKING',
    'GOOGLE_ADS',
    'SEARCH_CONSOLE',
    'SALESFEED',
    'SOCIAL_PLATFORMS',
    'KVK_HANDELSREGISTER',
    'CREDITSAFE',
    'COMPANY_INFO',
    /**
     * De LLM als *bron* (rol B, P8): websearch met verplichte URL-citaties. Staat
     * los van rol A (analyse/synthese), die geen bronfamilie heeft omdat die
     * uitsluitend opgeslagen feiten herformuleert.
     *
     * Staat in elke ladder **als laatste**: een AI-afgeleide waarde mag nooit een
     * register- of providerwaarde verdringen. Altijd tier C (`AI_GENERATED`) en
     * nooit `FACT` — zie `assertAiWebResearchLimits`.
     */
    'AI_WEB_RESEARCH',
    'MANUAL',
];
const CONNECTOR_SOURCE_FAMILY = {
    'CON-01': 'GOOGLE_SEARCH',
    'CON-02': 'SE_RANKING',
    'CON-03': 'GOOGLE_ADS',
    'CON-04': 'SEARCH_CONSOLE',
    'CON-05': 'SALESFEED',
    // Het Handelsregister is een eigen bronfamilie: een KVK-feit en een
    // website-afleiding zijn dus wél onafhankelijke bevestigingen van elkaar.
    'CON-08': 'KVK_HANDELSREGISTER',
    // Creditsafe is een eigen bronfamilie: onafhankelijk van zowel KVK als de
    // website (levert kredietscore en financiële kerncijfers die geen van
    // beide leveren).
    'CON-10': 'CREDITSAFE',
    // Company.info is een eigen bronfamilie: financiële kerncijfers uit officiële
    // financiële datasets, onafhankelijk van Creditsafe's kredietbureau-cijfers —
    // een echte tweede bron voor omzet/winst/balanstotaal (US06-kruisverificatie).
    'CON-11': 'COMPANY_INFO',
};
/**
 * Bepaalt de bronfamilie van een connector. Een aggregator (CON-07 Windsor.ai)
 * volgt de onderliggende bron en moet die expliciet meegeven; OpenAI (CON-06)
 * heeft geen zelfstandige bronkwaliteit en levert dus nooit een bronfamilie.
 */
export function resolveSourceFamily(connectorCode, underlyingFamily) {
    if (connectorCode === 'CON-07')
        return underlyingFamily ?? null;
    if (connectorCode === 'CON-06')
        return null;
    return CONNECTOR_SOURCE_FAMILY[connectorCode] ?? null;
}
/**
 * Welke connector schrijft deze bronfamilie? Omgekeerde van
 * `CONNECTOR_SOURCE_FAMILY`.
 *
 * `null` betekent "geen connector nodig": de websitecrawl heeft geen CON-code
 * en draait altijd, en `MANUAL` is per definitie mensenwerk. Die twee mogen
 * dus nooit als "bron staat uit" worden gelezen.
 */
export function connectorCodeForFamily(family) {
    if (family === 'AI_WEB_RESEARCH')
        return 'CON-06';
    const hit = Object.entries(CONNECTOR_SOURCE_FAMILY).find(([, f]) => f === family);
    return hit ? hit[0] : null;
}
/**
 * Kan deze metriek überhaupt nog gevuld worden, gegeven de connectors die mogen
 * draaien? (P18)
 *
 * Een metriek waarvan élke bron in de ladder een uitgeschakelde connector is,
 * levert geen rij op — en dan leest het rapport "deze uitvoering leverde geen
 * waarden op", wat onwaar is: er is niets geprobeerd. Met deze functie kan het
 * rapport het verschil benoemen.
 *
 * `runnableConnectors` bevat de codes die daadwerkelijk mogen draaien (catalogus
 * én databasevlag). Families zonder connector tellen altijd als beschikbaar.
 */
export function hasRunnableWriter(definition, runnableConnectors) {
    return definition.sourcePreference.some((family) => {
        // `MANUAL` telt niet mee. Elke ladder begint ermee, want een analist mag elke
        // waarde corrigeren — maar dat is geen bron die draait. Zou hij wél meetellen,
        // dan is élke metriek altijd "vulbaar" en kan het rapport nooit melden dat een
        // bron uit stond, wat precies de mededeling is die de lezer nodig heeft.
        if (family === 'MANUAL')
            return false;
        const code = connectorCodeForFamily(family);
        // `WEBSITE` heeft geen CON-code maar draait wel: de crawler hoort bij de
        // applicatie en staat nooit uit.
        return code === null || runnableConnectors.has(code);
    });
}
/** Alleen verschillende bronfamilies tellen als onafhankelijke bevestiging. */
export function areIndependentSources(a, b) {
    if (a === null || b === null)
        return false;
    return a !== b;
}
/**
 * Funderingsset: metrieken voor de reeds gebouwde websiteconnector (M6) en de
 * goedgekeurde connectors SE Ranking (CON-02) en Search Console (CON-04), plus
 * de positioneringsmetriek met bandbreedte (M17-voorbereiding). Uitbreiding
 * gebeurt per vergelijkingsmilestone (M13–M17); codes zijn stabiel.
 */
export const METRIC_CATALOG = [
    // Website (M6)
    {
        code: 'website.reachable',
        nameNl: 'Website bereikbaar',
        category: 'WEBSITE',
        valueType: 'BOOLEAN',
        defaultClassification: 'FACT',
        defaultProvenanceTier: 'SOURCED',
        direction: 'NEUTRAL',
        sourcePreference: ['WEBSITE'],
        freshnessWindowDays: 30,
        descriptionNl: 'Of de publieke website bij het ophalen bereikbaar was.',
    },
    {
        code: 'website.detected_services',
        nameNl: 'Diensten volgens eigen website',
        category: 'WEBSITE',
        valueType: 'LIST',
        defaultClassification: 'INFERENCE',
        defaultProvenanceTier: 'SOURCED',
        direction: 'NEUTRAL',
        sourcePreference: ['MANUAL', 'WEBSITE'],
        freshnessWindowDays: 180,
        descriptionNl: 'Diensten zoals afgeleid uit de publieke website; eigen claims, niet onafhankelijk.',
    },
    // SEO (CON-02 SE Ranking)
    {
        code: 'seo.organic_keywords',
        nameNl: 'Organische zoekwoorden',
        category: 'SEO',
        valueType: 'NUMBER',
        unit: 'aantal',
        defaultClassification: 'ESTIMATE',
        defaultProvenanceTier: 'SOURCED',
        direction: 'HIGHER_IS_BETTER',
        sourcePreference: ['MANUAL', 'SE_RANKING'],
        freshnessWindowDays: 90,
        descriptionNl: 'Aantal zoekwoorden waarop het domein organisch zichtbaar is (providerschatting).',
    },
    {
        code: 'seo.organic_traffic',
        nameNl: 'Geschat organisch verkeer',
        category: 'SEO',
        valueType: 'NUMBER',
        unit: 'bezoeken/maand',
        defaultClassification: 'ESTIMATE',
        defaultProvenanceTier: 'SOURCED',
        direction: 'HIGHER_IS_BETTER',
        sourcePreference: ['MANUAL', 'SE_RANKING'],
        freshnessWindowDays: 90,
        descriptionNl: 'Geschat organisch verkeer per maand; voor het eigen bedrijf gaat first-party data voor.',
    },
    {
        code: 'seo.backlinks',
        nameNl: 'Backlinks',
        category: 'SEO',
        valueType: 'NUMBER',
        unit: 'aantal',
        defaultClassification: 'ESTIMATE',
        defaultProvenanceTier: 'SOURCED',
        direction: 'HIGHER_IS_BETTER',
        sourcePreference: ['MANUAL', 'SE_RANKING'],
        freshnessWindowDays: 90,
        descriptionNl: 'Totaal aantal bekende backlinks naar het domein (providerschatting).',
    },
    {
        code: 'seo.referring_domains',
        nameNl: 'Verwijzende domeinen',
        category: 'SEO',
        valueType: 'NUMBER',
        unit: 'aantal',
        defaultClassification: 'ESTIMATE',
        defaultProvenanceTier: 'SOURCED',
        direction: 'HIGHER_IS_BETTER',
        sourcePreference: ['MANUAL', 'SE_RANKING'],
        freshnessWindowDays: 90,
        descriptionNl: 'Aantal unieke domeinen dat naar het domein verwijst (providerschatting).',
    },
    {
        code: 'seo.top_keywords',
        nameNl: 'Belangrijkste organische zoekwoorden',
        category: 'SEO',
        valueType: 'LIST',
        defaultClassification: 'ESTIMATE',
        defaultProvenanceTier: 'SOURCED',
        direction: 'NEUTRAL',
        sourcePreference: ['MANUAL', 'SE_RANKING'],
        freshnessWindowDays: 90,
        descriptionNl: 'Best presterende organische zoekwoorden van het domein.',
    },
    // ── SERP-overlap (P10b, SE Ranking /v1/domain/keywords/comparison) ────────
    //
    // Deze drie gaan over de *relatie* tussen een concurrent en het eigen bedrijf,
    // niet over het domein op zichzelf. Voor het eigen bedrijf zijn ze daarom
    // zinloos ("overlap met jezelf") en legt de werkerstap er expliciet
    // `NOT_APPLICABLE` vast — zonder die rij zou de dekkingsscore het eigen
    // bedrijf drie gaten aanrekenen die geen gaten zijn.
    {
        code: 'seo.shared_keywords',
        nameNl: 'Gedeelde zoekwoorden met het eigen bedrijf',
        category: 'SEO',
        valueType: 'NUMBER',
        unit: 'aantal',
        defaultClassification: 'ESTIMATE',
        defaultProvenanceTier: 'SOURCED',
        // Meer overlap is niet beter of slechter: het betekent dat de concurrentie
        // directer is. Een rangorde hierop zou een waardeoordeel suggereren dat er
        // niet is.
        direction: 'NEUTRAL',
        sourcePreference: ['MANUAL', 'SE_RANKING'],
        freshnessWindowDays: 90,
        descriptionNl: 'Aantal zoekwoorden waarop dit domein én het eigen bedrijf beide ranken (providerschatting). Blijft NOT_AVAILABLE wanneer de lijst de paginagrootte raakt, want dan is het aantal een ondergrens en geen aantal.',
    },
    {
        code: 'seo.shared_top_keywords',
        nameNl: 'Belangrijkste gedeelde zoekwoorden',
        category: 'SEO',
        valueType: 'LIST',
        defaultClassification: 'ESTIMATE',
        defaultProvenanceTier: 'SOURCED',
        direction: 'NEUTRAL',
        sourcePreference: ['MANUAL', 'SE_RANKING'],
        freshnessWindowDays: 90,
        descriptionNl: 'Gedeelde zoekwoorden met het hoogste zoekvolume, met beide posities. Dit is het signaal dat het kant-en-klare aantal niet geeft: waar de concurrentie werkelijk zit.',
    },
    {
        code: 'seo.shared_keywords_ahead',
        nameNl: 'Gedeelde zoekwoorden waarop dit domein hoger staat',
        category: 'SEO',
        valueType: 'NUMBER',
        unit: 'aantal',
        defaultClassification: 'ESTIMATE',
        defaultProvenanceTier: 'SOURCED',
        // Voor de concurrent gelezen: minder is beter voor het eigen bedrijf.
        direction: 'LOWER_IS_BETTER',
        sourcePreference: ['MANUAL', 'SE_RANKING'],
        freshnessWindowDays: 90,
        descriptionNl: 'Aantal gedeelde zoekwoorden waarop dit domein een betere positie heeft dan het eigen bedrijf. Zonder dit getal zegt "500 gedeelde zoekwoorden" niets over wie er wint.',
    },
    // Search Console (CON-04, first-party, alleen eigen bedrijf)
    // SEA (M14): indicaties van advertentie-activiteit uit toegestane externe
    // data (SE Ranking adv-dataset). Eigen Google Ads-feiten volgen pas na
    // goedkeuring van CON-03; die krijgen dan GOOGLE_ADS als voorkeursbron.
    {
        code: 'sea.paid_keywords',
        nameNl: 'Betaalde zoekwoorden (indicatie)',
        category: 'SEA',
        valueType: 'NUMBER',
        unit: 'aantal',
        defaultClassification: 'ESTIMATE',
        defaultProvenanceTier: 'SOURCED',
        direction: 'NEUTRAL',
        sourcePreference: ['MANUAL', 'SE_RANKING'],
        freshnessWindowDays: 90,
        descriptionNl: 'Aantal zoekwoorden waarop het domein adverteert volgens externe data; indicatie van SEA-activiteit, geen accountfeit.',
    },
    {
        code: 'sea.paid_traffic',
        nameNl: 'Geschat betaald verkeer (indicatie)',
        category: 'SEA',
        valueType: 'NUMBER',
        unit: 'bezoeken/maand',
        defaultClassification: 'ESTIMATE',
        defaultProvenanceTier: 'SOURCED',
        direction: 'NEUTRAL',
        sourcePreference: ['MANUAL', 'SE_RANKING'],
        freshnessWindowDays: 90,
        descriptionNl: 'Geschat verkeer uit betaalde zoekresultaten volgens externe data; indicatie van SEA-activiteit, geen accountfeit.',
    },
    // Google Ads first-party (C03/CON-03): echte accountprestaties van het eigen
    // bedrijf — feiten, uitsluitend voor COMPANY (companyOnly), nooit concurrenten.
    // Positionering (M17 / US05F): producten/diensten staan al in
    // `website.detected_services`; hieronder de overige positioneringsdimensies,
    // deterministisch afgeleid uit de publieke website per subject. Het zijn eigen
    // claims van de bron — dus classificatie INFERENCE en niet onafhankelijk.
    {
        code: 'positioning.propositions',
        nameNl: 'Kernproposities',
        category: 'POSITIONING',
        valueType: 'LIST',
        defaultClassification: 'INFERENCE',
        defaultProvenanceTier: 'SOURCED',
        direction: 'NEUTRAL',
        sourcePreference: ['MANUAL', 'WEBSITE'],
        freshnessWindowDays: 180,
        descriptionNl: 'Belangrijkste proposities/USP’s zoals afgeleid uit de publieke website; eigen claims, niet onafhankelijk geverifieerd.',
    },
    {
        code: 'positioning.geographic_focus',
        nameNl: 'Geografische focus',
        category: 'POSITIONING',
        valueType: 'TEXT',
        defaultClassification: 'INFERENCE',
        defaultProvenanceTier: 'SOURCED',
        direction: 'NEUTRAL',
        sourcePreference: ['MANUAL', 'KVK_HANDELSREGISTER', 'WEBSITE'],
        freshnessWindowDays: 365,
        descriptionNl: 'Geografisch werkgebied zoals afgeleid uit de vestigingsplaats (KVK) of de publieke website.',
    },
    {
        code: 'positioning.industry',
        nameNl: 'Branche-indicatie',
        category: 'POSITIONING',
        valueType: 'TEXT',
        // Blijft INFERENCE als standaard (websiteafleiding); de KVK-connector levert
        // de SBI-hoofdactiviteit expliciet als FACT aan.
        defaultClassification: 'INFERENCE',
        defaultProvenanceTier: 'SOURCED',
        direction: 'NEUTRAL',
        sourcePreference: ['MANUAL', 'KVK_HANDELSREGISTER', 'WEBSITE'],
        freshnessWindowDays: 365,
        descriptionNl: 'Branche/sector: SBI-hoofdactiviteit uit het Handelsregister (feit) of, zonder KVK-nummer, afgeleid uit de publieke website (indicatief).',
    },
    // Registerfeiten uit het Handelsregister (CON-08). Deze staan zwart-op-wit in
    // een officieel register en zijn dus FACT — geen schatting en geen websitegok.
    {
        code: 'positioning.legal_form',
        nameNl: 'Rechtsvorm',
        category: 'POSITIONING',
        valueType: 'TEXT',
        defaultClassification: 'FACT',
        defaultProvenanceTier: 'SOURCED',
        direction: 'NEUTRAL',
        sourcePreference: ['MANUAL', 'KVK_HANDELSREGISTER'],
        freshnessWindowDays: 365,
        descriptionNl: 'Rechtsvorm volgens het Handelsregister (bijv. Besloten Vennootschap, Eenmanszaak).',
    },
    {
        code: 'positioning.employees',
        nameNl: 'Werkzame personen',
        category: 'POSITIONING',
        valueType: 'NUMBER',
        unit: 'personen',
        defaultClassification: 'FACT',
        defaultProvenanceTier: 'SOURCED',
        direction: 'NEUTRAL',
        sourcePreference: ['MANUAL', 'KVK_HANDELSREGISTER'],
        freshnessWindowDays: 365,
        descriptionNl: 'Totaal aantal werkzame personen volgens het Handelsregister. Niet bij elke inschrijving bekend; dan NOT_AVAILABLE en nooit 0.',
    },
    {
        code: 'positioning.founded_year',
        nameNl: 'Jaar van inschrijving',
        category: 'POSITIONING',
        valueType: 'NUMBER',
        defaultClassification: 'FACT',
        defaultProvenanceTier: 'SOURCED',
        direction: 'NEUTRAL',
        sourcePreference: ['MANUAL', 'KVK_HANDELSREGISTER'],
        // Een inschrijvingsjaar verandert niet meer; ruim actualiteitsvenster.
        freshnessWindowDays: 1825,
        descriptionNl: 'Jaar van formele registratie in het Handelsregister; een indicatie van bedrijfsleeftijd, niet per definitie het oprichtingsjaar.',
    },
    {
        code: 'positioning.establishment_count',
        nameNl: 'Aantal vestigingen',
        category: 'POSITIONING',
        valueType: 'NUMBER',
        unit: 'vestigingen',
        defaultClassification: 'FACT',
        defaultProvenanceTier: 'SOURCED',
        // Meer vestigingen is niet beter of slechter, alleen een andere spreiding.
        direction: 'NEUTRAL',
        sourcePreference: ['MANUAL', 'KVK_HANDELSREGISTER'],
        freshnessWindowDays: 365,
        descriptionNl: 'Aantal in het Handelsregister ingeschreven vestigingen; een signaal voor geografische spreiding. Niet opgevraagd blijft NOT_QUERIED, nooit 0.',
    },
    // Omzetinschatting: uitsluitend met bandbreedte, bron en schattingslabel.
    // Kredietscore en financiële kerncijfers uit Creditsafe (CON-10). Vult het
    // gat dat CON-08 bewust openlaat: KVK levert nooit omzet of een
    // kredietoordeel. Losstaand van `positioning.revenue_estimate` (dat is een
    // AI-/websiteschatting als bandbreedte; dit is een gerapporteerd cijfer).
    {
        code: 'positioning.company_status',
        nameNl: 'Bedrijfsstatus',
        category: 'POSITIONING',
        valueType: 'TEXT',
        defaultClassification: 'FACT',
        defaultProvenanceTier: 'SOURCED',
        direction: 'NEUTRAL',
        sourcePreference: ['MANUAL', 'CREDITSAFE'],
        freshnessWindowDays: 180,
        descriptionNl: 'Bedrijfsstatus (bijv. actief/inactief) volgens Creditsafe.',
    },
    {
        code: 'positioning.credit_rating',
        nameNl: 'Kredietrating',
        category: 'POSITIONING',
        valueType: 'TEXT',
        defaultClassification: 'FACT',
        defaultProvenanceTier: 'SOURCED',
        direction: 'NEUTRAL',
        sourcePreference: ['MANUAL', 'CREDITSAFE'],
        freshnessWindowDays: 180,
        descriptionNl: 'Kredietrating (waardering + omschrijving) zoals gerapporteerd door Creditsafe.',
    },
    {
        code: 'positioning.credit_limit',
        nameNl: 'Kredietlimiet',
        category: 'POSITIONING',
        valueType: 'NUMBER',
        unit: 'EUR',
        defaultClassification: 'FACT',
        defaultProvenanceTier: 'SOURCED',
        direction: 'NEUTRAL',
        sourcePreference: ['MANUAL', 'CREDITSAFE'],
        freshnessWindowDays: 180,
        descriptionNl: 'Geadviseerde kredietlimiet zoals gerapporteerd door Creditsafe.',
    },
    {
        code: 'positioning.revenue',
        nameNl: 'Omzet',
        category: 'POSITIONING',
        valueType: 'NUMBER',
        unit: 'EUR/jaar',
        defaultClassification: 'FACT',
        defaultProvenanceTier: 'SOURCED',
        direction: 'NEUTRAL',
        // Creditsafe (kredietbureau-cijfers) en Company.info (officiële financiële
        // datasets) zijn onafhankelijke bronnen voor dezelfde grootheid — bewust
        // beide toegestaan zodat US06 afwijkingen kan signaleren i.p.v. verbergen.
        sourcePreference: ['MANUAL', 'CREDITSAFE'],
        freshnessWindowDays: 365,
        descriptionNl: 'Omzet uit de meest recente jaarcijfers zoals gerapporteerd door Creditsafe of Company.info.',
    },
    {
        code: 'positioning.equity',
        nameNl: 'Eigen vermogen',
        category: 'POSITIONING',
        valueType: 'NUMBER',
        unit: 'EUR',
        defaultClassification: 'FACT',
        defaultProvenanceTier: 'SOURCED',
        direction: 'NEUTRAL',
        sourcePreference: ['MANUAL', 'CREDITSAFE'],
        freshnessWindowDays: 365,
        descriptionNl: 'Eigen vermogen uit de meest recente jaarcijfers zoals gerapporteerd door Creditsafe.',
    },
    {
        code: 'positioning.solvency_ratio',
        nameNl: 'Solvabiliteit',
        category: 'POSITIONING',
        valueType: 'NUMBER',
        unit: '%',
        defaultClassification: 'FACT',
        defaultProvenanceTier: 'SOURCED',
        direction: 'NEUTRAL',
        sourcePreference: ['MANUAL', 'CREDITSAFE'],
        freshnessWindowDays: 365,
        descriptionNl: 'Solvabiliteit (eigen vermogen als percentage van het balanstotaal) uit de meest recente jaarcijfers.',
    },
    // Company.info (CON-11): financiële kerncijfers die Creditsafe in de huidige
    // CON-10-scope niet levert (winst, balanstotaal, brutomarge) — uit officiële
    // financiële datasets, dus een andere bronfamilie dan Creditsafe.
    // First-party websitebezoek (C05 Salesfeed): uitsluitend het eigen bedrijf,
    // uitsluitend organisatieniveau (nooit persoons-/IP-gegevens).
    // Social/marketing (C07 Windsor.ai, M16): first-party accountdata van
    // platformen zonder eigen directe connector. Windsor.ai levert geen
    // zelfstandige bronfamilie (zie resolveSourceFamily) — de connector geeft
    // per configuratie de onderliggende bron (bijv. SOCIAL_PLATFORMS) mee.
    // Rol B — de LLM als bron (P8, PLAN.md §9). Altijd de laatste tree van de
    // ladder en altijd tier C: een AI-afgeleide waarde mag nooit een register- of
    // providerwaarde verdringen, en moet in het rapport als zodanig herkenbaar zijn.
    {
        code: 'positioning.named_customers',
        nameNl: 'Genoemde klanten en referenties',
        category: 'POSITIONING',
        valueType: 'LIST',
        defaultClassification: 'INFERENCE',
        // De eigen website is de voorkeursbron en die is tier A; alleen wanneer de
        // LLM hem vult schrijft die writer expliciet AI_GENERATED.
        defaultProvenanceTier: 'SOURCED',
        direction: 'NEUTRAL',
        sourcePreference: ['MANUAL', 'WEBSITE', 'AI_WEB_RESEARCH'],
        freshnessWindowDays: 180,
        descriptionNl: 'Klanten of referenties die het bedrijf zelf noemt (website), of die in openbare bronnen aan het bedrijf worden gekoppeld (AI-onderzoek). Nooit een klantenlijst — alleen wat publiek is gemaakt.',
    },
    {
        code: 'positioning.product_weaknesses',
        nameNl: 'Genoemde zwaktes in aanbod',
        category: 'POSITIONING',
        valueType: 'LIST',
        defaultClassification: 'INFERENCE',
        // Tier C per definitie: geen bron in deze set levert dit als feit.
        defaultProvenanceTier: 'AI_GENERATED',
        direction: 'NEUTRAL',
        sourcePreference: ['MANUAL', 'AI_WEB_RESEARCH'],
        freshnessWindowDays: 180,
        descriptionNl: 'Zwaktes of beperkingen die in openbare bronnen aan het aanbod worden toegeschreven. Altijd AI-afgeleid en altijd met geciteerde bron; geen eigen oordeel.',
    },
    {
        code: 'positioning.recent_signals',
        nameNl: 'Recente signalen',
        category: 'POSITIONING',
        valueType: 'LIST',
        defaultClassification: 'INFERENCE',
        defaultProvenanceTier: 'AI_GENERATED',
        direction: 'NEUTRAL',
        sourcePreference: ['MANUAL', 'AI_WEB_RESEARCH'],
        // Kort venster: een "recent signaal" van een jaar oud is geen signaal meer.
        freshnessWindowDays: 90,
        descriptionNl: 'Recente openbare signalen zoals uitbreiding, investering, overname of opvallende vacatures, elk met geciteerde bron.',
    },
    {
        code: 'reputation.review_score',
        nameNl: 'Reviewscore',
        category: 'REPUTATION',
        valueType: 'NUMBER',
        unit: 'score',
        // ESTIMATE, nooit FACT: het is een samenvatting van meerdere platforms met
        // verschillende schalen, niet een gemeten getal.
        defaultClassification: 'ESTIMATE',
        defaultProvenanceTier: 'AI_GENERATED',
        direction: 'HIGHER_IS_BETTER',
        sourcePreference: ['MANUAL', 'AI_WEB_RESEARCH'],
        freshnessWindowDays: 90,
        descriptionNl: 'Gemiddelde openbare reviewscore, genormaliseerd naar een schaal van 1 tot 5, met de geciteerde bron(nen). Ontbreekt een bron, dan wordt de waarde niet opgeslagen.',
    },
    {
        code: 'reputation.review_sentiment',
        nameNl: 'Reviewsentiment',
        category: 'REPUTATION',
        valueType: 'TEXT',
        defaultClassification: 'INFERENCE',
        defaultProvenanceTier: 'AI_GENERATED',
        direction: 'NEUTRAL',
        sourcePreference: ['MANUAL', 'AI_WEB_RESEARCH'],
        freshnessWindowDays: 90,
        descriptionNl: 'Terugkerende thema\u2019s in openbare reviews, samengevat met geciteerde bron(nen). Altijd AI-afgeleid.',
    },
];
/**
 * P5 — metrieken zonder writer (PLAN.md §0 en §12).
 *
 * Deze zestien metrieken verloren hun enige bron toen CON-03 (Google Ads),
 * CON-04 (Search Console), CON-05 (Salesfeed), CON-07 (Windsor.ai) en CON-11
 * (Company.info) op `DISABLED` gingen; `positioning.revenue_estimate` had er al
 * geen. Ze staan **niet** in `METRIC_CATALOG`, want een metriek in de catalogus
 * zonder bron belooft dekking die er niet is: het rapport toont dan een rij die
 * nooit gevuld kan worden en de dekkingsscore rekent hem mee (AUDIT.md §8).
 *
 * Ze blijven hier wél staan, met hun oorspronkelijke definitie, om twee redenen:
 * heractiveren van een connector is dan één commit die de betreffende regels
 * terugzet, en `normalization` kan een geweigerde observatie onderscheiden van
 * een typefout — "bron uitgeschakeld" is een andere diagnose dan "onbekende
 * metriekcode".
 *
 * Codes worden nooit hergebruikt voor iets anders: er staan historische
 * observaties met deze codes in de database.
 */
export const RETIRED_METRICS = [
    {
        code: 'search_console.clicks',
        nameNl: 'Zoekclicks (eigen site)',
        category: 'SEARCH_CONSOLE',
        valueType: 'NUMBER',
        unit: 'aantal',
        defaultClassification: 'FACT',
        defaultProvenanceTier: 'SOURCED',
        direction: 'HIGHER_IS_BETTER',
        sourcePreference: ['SEARCH_CONSOLE'],
        freshnessWindowDays: 35,
        companyOnly: true,
        descriptionNl: 'First-party clicks uit Google Zoeken over de meetperiode; alleen het eigen bedrijf.',
    },
    {
        code: 'search_console.impressions',
        nameNl: 'Zoekvertoningen (eigen site)',
        category: 'SEARCH_CONSOLE',
        valueType: 'NUMBER',
        unit: 'aantal',
        defaultClassification: 'FACT',
        defaultProvenanceTier: 'SOURCED',
        direction: 'HIGHER_IS_BETTER',
        sourcePreference: ['SEARCH_CONSOLE'],
        freshnessWindowDays: 35,
        companyOnly: true,
        descriptionNl: 'First-party vertoningen in Google Zoeken over de meetperiode; alleen het eigen bedrijf.',
    },
    {
        code: 'search_console.ctr',
        nameNl: 'Doorklikratio (eigen site)',
        category: 'SEARCH_CONSOLE',
        valueType: 'NUMBER',
        unit: 'procent',
        defaultClassification: 'FACT',
        defaultProvenanceTier: 'SOURCED',
        direction: 'HIGHER_IS_BETTER',
        sourcePreference: ['SEARCH_CONSOLE'],
        freshnessWindowDays: 35,
        companyOnly: true,
        descriptionNl: 'First-party CTR (clicks/vertoningen) over de meetperiode; alleen het eigen bedrijf.',
    },
    {
        code: 'search_console.average_position',
        nameNl: 'Gemiddelde positie (eigen site)',
        category: 'SEARCH_CONSOLE',
        valueType: 'NUMBER',
        unit: 'positie',
        defaultClassification: 'FACT',
        defaultProvenanceTier: 'SOURCED',
        direction: 'LOWER_IS_BETTER',
        sourcePreference: ['SEARCH_CONSOLE'],
        freshnessWindowDays: 35,
        companyOnly: true,
        descriptionNl: 'Gemiddelde organische positie in Google Zoeken; alleen het eigen bedrijf.',
    },
    {
        code: 'sea.paid_clicks',
        nameNl: 'Betaalde klikken (eigen account)',
        category: 'SEA',
        valueType: 'NUMBER',
        unit: 'aantal',
        defaultClassification: 'FACT',
        defaultProvenanceTier: 'SOURCED',
        direction: 'HIGHER_IS_BETTER',
        sourcePreference: ['GOOGLE_ADS'],
        freshnessWindowDays: 35,
        companyOnly: true,
        descriptionNl: 'First-party klikken uit Google Ads over de meetperiode; alleen het eigen account.',
    },
    {
        code: 'sea.paid_impressions',
        nameNl: 'Betaalde vertoningen (eigen account)',
        category: 'SEA',
        valueType: 'NUMBER',
        unit: 'aantal',
        defaultClassification: 'FACT',
        defaultProvenanceTier: 'SOURCED',
        direction: 'HIGHER_IS_BETTER',
        sourcePreference: ['GOOGLE_ADS'],
        freshnessWindowDays: 35,
        companyOnly: true,
        descriptionNl: 'First-party advertentievertoningen uit Google Ads over de meetperiode; alleen het eigen account.',
    },
    {
        code: 'sea.paid_cost',
        nameNl: 'Advertentiekosten (eigen account)',
        category: 'SEA',
        valueType: 'NUMBER',
        unit: 'EUR',
        defaultClassification: 'FACT',
        defaultProvenanceTier: 'SOURCED',
        direction: 'NEUTRAL',
        sourcePreference: ['GOOGLE_ADS'],
        freshnessWindowDays: 35,
        companyOnly: true,
        descriptionNl: 'First-party advertentiekosten uit Google Ads over de meetperiode (accountvaluta); alleen het eigen account.',
    },
    {
        code: 'positioning.revenue_estimate',
        nameNl: 'Omzetindicatie',
        category: 'POSITIONING',
        valueType: 'RANGE',
        unit: 'EUR/jaar',
        defaultClassification: 'ESTIMATE',
        defaultProvenanceTier: 'SOURCED',
        direction: 'NEUTRAL',
        sourcePreference: ['MANUAL', 'WEBSITE'],
        freshnessWindowDays: 365,
        descriptionNl: 'Omzetinschatting, uitsluitend als bandbreedte met bron- en schattingslabel.',
    },
    {
        code: 'positioning.profit',
        nameNl: 'Winst',
        category: 'POSITIONING',
        valueType: 'NUMBER',
        unit: 'EUR',
        defaultClassification: 'FACT',
        defaultProvenanceTier: 'SOURCED',
        direction: 'HIGHER_IS_BETTER',
        sourcePreference: ['MANUAL', 'COMPANY_INFO'],
        freshnessWindowDays: 365,
        descriptionNl: 'Winst uit de meest recente jaarcijfers zoals gerapporteerd door Company.info.',
    },
    {
        code: 'positioning.balance_total',
        nameNl: 'Balanstotaal',
        category: 'POSITIONING',
        valueType: 'NUMBER',
        unit: 'EUR',
        defaultClassification: 'FACT',
        defaultProvenanceTier: 'SOURCED',
        direction: 'NEUTRAL',
        sourcePreference: ['MANUAL', 'COMPANY_INFO'],
        freshnessWindowDays: 365,
        descriptionNl: 'Balanstotaal (totale activa) uit de meest recente jaarcijfers zoals gerapporteerd door Company.info.',
    },
    {
        code: 'positioning.gross_margin',
        nameNl: 'Brutomarge',
        category: 'POSITIONING',
        valueType: 'NUMBER',
        unit: 'EUR',
        defaultClassification: 'FACT',
        defaultProvenanceTier: 'SOURCED',
        direction: 'NEUTRAL',
        sourcePreference: ['MANUAL', 'COMPANY_INFO'],
        freshnessWindowDays: 365,
        descriptionNl: 'Brutomarge uit de meest recente jaarcijfers zoals gerapporteerd door Company.info.',
    },
    {
        code: 'first_party.recognized_organizations_count',
        nameNl: 'Herkende organisaties (websitebezoek)',
        category: 'FIRST_PARTY',
        valueType: 'NUMBER',
        defaultClassification: 'FACT',
        defaultProvenanceTier: 'SOURCED',
        direction: 'HIGHER_IS_BETTER',
        sourcePreference: ['MANUAL', 'SALESFEED'],
        freshnessWindowDays: 30,
        companyOnly: true,
        descriptionNl: 'Aantal unieke organisaties dat Salesfeed heeft herkend als bezoeker van de eigen website binnen de meetperiode.',
    },
    {
        code: 'first_party.recognized_organizations',
        nameNl: 'Namen van herkende organisaties',
        category: 'FIRST_PARTY',
        valueType: 'LIST',
        defaultClassification: 'FACT',
        defaultProvenanceTier: 'SOURCED',
        direction: 'NEUTRAL',
        sourcePreference: ['MANUAL', 'SALESFEED'],
        freshnessWindowDays: 30,
        companyOnly: true,
        descriptionNl: 'Namen van organisaties die Salesfeed heeft herkend als bezoeker van de eigen website binnen de meetperiode (organisatieniveau, geen persoons- of IP-gegevens).',
    },
    {
        code: 'social.followers',
        nameNl: 'Volgers (social)',
        category: 'SOCIAL',
        valueType: 'NUMBER',
        defaultClassification: 'FACT',
        defaultProvenanceTier: 'SOURCED',
        direction: 'HIGHER_IS_BETTER',
        sourcePreference: ['MANUAL', 'SOCIAL_PLATFORMS'],
        freshnessWindowDays: 30,
        companyOnly: true,
        descriptionNl: 'Aantal volgers op het gekoppelde social-mediaplatform van het eigen account.',
    },
    {
        code: 'social.engagement_rate',
        nameNl: 'Engagementratio (social)',
        category: 'SOCIAL',
        valueType: 'NUMBER',
        unit: '%',
        defaultClassification: 'FACT',
        defaultProvenanceTier: 'SOURCED',
        direction: 'HIGHER_IS_BETTER',
        sourcePreference: ['MANUAL', 'SOCIAL_PLATFORMS'],
        freshnessWindowDays: 30,
        companyOnly: true,
        descriptionNl: 'Engagementratio (interacties t.o.v. bereik/volgers) op het gekoppelde social-mediaplatform van het eigen account.',
    },
    {
        code: 'social.posts_count',
        nameNl: 'Aantal posts (social)',
        category: 'SOCIAL',
        valueType: 'NUMBER',
        defaultClassification: 'FACT',
        defaultProvenanceTier: 'SOURCED',
        direction: 'NEUTRAL',
        sourcePreference: ['MANUAL', 'SOCIAL_PLATFORMS'],
        freshnessWindowDays: 30,
        companyOnly: true,
        descriptionNl: 'Aantal geplaatste posts op het gekoppelde social-mediaplatform van het eigen account binnen de meetperiode.',
    },
];
const CATALOG_BY_CODE = new Map(METRIC_CATALOG.map((m) => [m.code, m]));
const RETIRED_BY_CODE = new Map(RETIRED_METRICS.map((m) => [m.code, m]));
export function getMetricDefinition(code) {
    return CATALOG_BY_CODE.get(code);
}
export function isKnownMetric(code) {
    return CATALOG_BY_CODE.has(code);
}
/**
 * Bestond deze metriek maar heeft ze geen bron meer (P5)?
 *
 * Bedoeld om een geweigerde observatie te kunnen diagnosticeren: een
 * uitgeschakelde connector die nog draait is een configuratiefout, een
 * onbekende code is een typefout of een versieverschil. Zonder dit onderscheid
 * zien beide er in de log identiek uit.
 */
export function isRetiredMetric(code) {
    return RETIRED_BY_CODE.has(code);
}
/**
 * Definitie van een metriek, ook wanneer die is uitgezet. Voor weergave van
 * historische observaties: die blijven in de database staan en hebben nog een
 * naam en eenheid nodig.
 */
export function getMetricDefinitionIncludingRetired(code) {
    return CATALOG_BY_CODE.get(code) ?? RETIRED_BY_CODE.get(code);
}
/**
 * Strikte scheiding tussen eigen feiten en concurrentieschattingen (M14):
 * first-party metrieken (`companyOnly`) mogen uitsluitend voor het eigen
 * bedrijf worden opgeslagen, nooit voor een concurrent.
 */
export function isMetricAllowedForSubject(code, subjectType) {
    const def = CATALOG_BY_CODE.get(code);
    if (!def)
        return false;
    return isDefinitionAllowedForSubject(def, subjectType);
}
/**
 * Dezelfde regel, maar op een definitie in plaats van een code.
 *
 * Bestaat los omdat er sinds P5 geen enkele `companyOnly`-metriek meer in de
 * levende catalogus staat (CON-04 was de laatste): via `isMetricAllowedForSubject`
 * is de regel dan niet meer te testen, en een ongeteste regel rot weg. Komt er
 * ooit weer een first-party bron, dan moet deze scheiding meteen kloppen.
 */
export function isDefinitionAllowedForSubject(def, subjectType) {
    if (def.companyOnly)
        return subjectType === 'COMPANY';
    return true;
}
/**
 * De harde grenzen van de LLM als bron (rol B, P8, PLAN.md §9).
 *
 * Waarom dit een functie is en niet alleen een afspraak in de prompt: de LLM
 * levert tekst, en tekst kan alles beweren. Deze drie regels zijn de reden dat
 * een AI-waarde het rapport in mag zonder dat een lezer hem met een registerfeit
 * kan verwarren:
 *
 *  1. **Altijd tier C.** `AI_GENERATED`, zodat het label in de celtekst belandt.
 *  2. **Nooit `FACT`.** Alleen `ESTIMATE` of `INFERENCE`.
 *  3. **Nooit zonder geciteerde bron-URL.** Geen URL betekent geen waarde.
 *
 * Retourneert een lijst overtredingen; leeg betekent toegestaan.
 */
export function assertAiWebResearchLimits(input) {
    if (input.sourceFamily !== 'AI_WEB_RESEARCH')
        return [];
    const problems = [];
    if (input.provenanceTier !== 'AI_GENERATED') {
        problems.push(`AI-websearch moet tier AI_GENERATED hebben, niet ${input.provenanceTier}.`);
    }
    if (input.classification === 'FACT') {
        problems.push('AI-websearch mag nooit als FACT worden opgeslagen.');
    }
    if (input.citationCount <= 0) {
        problems.push('AI-websearch zonder geciteerde bron-URL levert geen waarde op.');
    }
    return problems;
}
/**
 * Staat `AI_WEB_RESEARCH` op de laatste plek van elke ladder waarin hij voorkomt?
 *
 * Een AI-afgeleide waarde mag nooit een register- of providerwaarde verdringen.
 * Als invariant afgedwongen in plaats van als afspraak, omdat één verkeerd
 * geordende ladder stil de voorkeur zou omdraaien.
 */
export function aiWebResearchIsLowestRung() {
    return METRIC_CATALOG.every((metric) => {
        const index = metric.sourcePreference.indexOf('AI_WEB_RESEARCH');
        return index === -1 || index === metric.sourcePreference.length - 1;
    });
}
//# sourceMappingURL=metrics.js.map